import React, { useState, useRef, useEffect } from 'react';
import { authApi } from '../firebase/config.js';
import { ENV } from '../config/env.js';

const API_BASE_URL = ENV.apiBaseUrl;

// Kept in module scope (not React state) so a conversation survives closing and
// reopening the tutor for the same folder during the tab's session, without
// needing any backend persistence. Resets on page reload.
const chatHistoryCache = new Map();

export default function FolderChatSidebar({ folder, onClose }) {
    const greeting = { role: 'assistant', content: `Hi! I'm your study tutor for **${folder.name}**. I know all ${(folder.cards || []).length} cards in this set. Ask me anything — definitions, comparisons, why something is true, mnemonics, or quiz me!` };
    const [messages, setMessages] = useState(() => chatHistoryCache.get(folder.id) || [greeting]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        chatHistoryCache.set(folder.id, messages);
    }, [folder.id, messages]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const send = async () => {
        const text = input.trim();
        if (!text || loading) return;
        const userMsg = { role: 'user', content: text };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);
        try {
            const history = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));
            const data = await authApi(`${API_BASE_URL}/api/folders/${folder.id}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: text, history }),
            });
            setMessages(prev => [...prev, { role: 'assistant', content: data.reply || 'Sorry, I couldn\'t respond.' }]);
        } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message || 'Failed to connect.'}` }]);
        } finally {
            setLoading(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    };

    return (
        <>
            {/* Backdrop — click anywhere outside the panel to close */}
            <div className="fixed inset-0 z-40" onClick={onClose} />

            <div className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-slate-900/98 border-l border-white/10 shadow-2xl flex flex-col backdrop-blur-sm">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-7 w-7 shrink-0 rounded-lg bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                        <svg className="h-3.5 w-3.5 text-violet-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">Study Tutor</p>
                        <p className="text-[11px] text-slate-400 truncate">{folder.name}</p>
                    </div>
                </div>
                <button onClick={onClose} className="shrink-0 rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                            m.role === 'user'
                                ? 'bg-violet-600 text-white rounded-br-sm'
                                : 'bg-slate-800/70 text-slate-200 border border-white/8 rounded-bl-sm'
                        }`}>
                            {m.content.split('\n').map((line, j) => (
                                <span key={j}>{line}{j < m.content.split('\n').length - 1 && <br />}</span>
                            ))}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="flex justify-start">
                        <div className="bg-slate-800/70 border border-white/8 rounded-2xl rounded-bl-sm px-4 py-2.5">
                            <span className="flex gap-1">
                                <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                                <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                                <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                            </span>
                        </div>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {/* Suggestions */}
            {messages.length === 1 && (
                <div className="px-4 pb-2 shrink-0">
                    <p className="text-[10px] text-slate-500 mb-2 uppercase tracking-wider">Try asking</p>
                    <div className="flex flex-wrap gap-1.5">
                        {[
                            'Quiz me on this set',
                            'What are the hardest concepts here?',
                            'Give me a mnemonic for the first card',
                            'Summarize the key themes',
                        ].map(q => (
                            <button key={q} onClick={() => { setInput(q); inputRef.current?.focus(); }}
                                className="text-xs rounded-full bg-white/5 hover:bg-white/10 border border-white/8 px-3 py-1 text-slate-300 transition">
                                {q}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Input */}
            <div className="shrink-0 flex items-end gap-2 px-4 py-3 border-t border-white/8">
                <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={1}
                    placeholder="Ask anything… (Enter to send)"
                    style={{ maxHeight: '100px', resize: 'none' }}
                    className="flex-1 rounded-xl bg-slate-800/60 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                />
                <button onClick={send} disabled={!input.trim() || loading}
                    className="shrink-0 h-9 w-9 rounded-full bg-violet-600 hover:bg-violet-500 disabled:opacity-40 flex items-center justify-center transition">
                    <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
            </div>
        </>
    );
}
